import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import WebsiteLogo from "@/components/common/WebsiteLogo";
import { useAuth } from "@/context/AuthContext";
import { getAuth, GoogleAuthProvider, signInWithPopup, createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import { sendEmailVerificationWithContinueUrl } from "@/services/authVerification";
import { getCampOrganization } from "@/services/campOrganizationQuery";
import { persistInviteToAccountIfNeeded } from "@/auth/inviteAuth";
import { db } from "@/services/firebaseService";
import { ensureUserIdentityBaseline, IdentityBaselineSource } from "@/services/userIdentityBaselineService";
import { mergeUserAllowedFieldsAfterSignup } from "@/services/userProfileAllowedWrites";
import { enforceProAccessGate } from "@/lib/proAccessGate";
import { mintApexSession } from "@/lib/apexSessionMint";
import { z } from "zod";
import TermsOfUseModal from "@/components/welcome/components/TermsOfUseModal";
import { Copyright } from "lucide-react";
import { Separator } from "@/components/ui/separator";

// Form validation schema
const createAccountSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  password: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string().min(6, "Please confirm your password"),
  agreeToTerms: z.boolean().refine(val => val === true, "You must agree to the Terms of Use")
}).refine(data => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"]
});

const CreateAccountPage = () => {
  const navigate = useNavigate();
  const { currentUser, signOut } = useAuth();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showFooterTermsModal, setShowFooterTermsModal] = useState(false);
  const [hasOpenedTerms, setHasOpenedTerms] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  // Load organization name from custom claims
  useEffect(() => {
    const loadOrganizationName = async () => {
      if (currentUser?.isAnonymous) {
        try {
          const tokenResult = await currentUser.getIdTokenResult();
          const organizationId = tokenResult.claims.organizationId as string;
          
          if (organizationId) {
            const org = await getCampOrganization(db, organizationId);
            if (org) {
              setOrganizationName(org.name);
            }
          }
        } catch (error) {
          console.error("Error loading organization name:", error);
        }
      }
    };

    loadOrganizationName();
  }, [currentUser]);

  // Redirect if not anonymous user
  useEffect(() => {
    if (!currentUser?.isAnonymous) {
      navigate('/login');
    }
  }, [currentUser, navigate]);

  const validateForm = () => {
    try {
      createAccountSchema.parse({
        email,
        firstName,
        lastName,
        password,
        confirmPassword,
        agreeToTerms
      });
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path) {
            newErrors[err.path[0]] = err.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  // Only validate form after user has attempted to submit
  useEffect(() => {
    if (hasAttemptedSubmit) {
      validateForm();
    }
  }, [email, firstName, lastName, password, confirmPassword, agreeToTerms, hasAttemptedSubmit]);

  /**
   * Returns true if the new account was admitted into the app, false if the
   * allowlist gate refused it. Throws only on a genuine failure to create the
   * account — a refusal is an outcome, not an error, and must not reach the
   * callers' "Failed to create account. Please try again." catch.
   */
  const createUserAccount = async (user: any, authMethod: "email" | "google"): Promise<boolean> => {
    try {
      // Get the current token BEFORE it gets removed by persistInviteToAccountIfNeeded
      const token = localStorage.getItem('kb_invite_token'); // SECURITY_AUDIT_PASS: localStorage key name only, not a secret

      // First, persist invitation to account to set up proper permissions
      await persistInviteToAccountIfNeeded();

      // Wait a moment for the custom claims to propagate
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Refresh the token to get updated claims
      await user.getIdToken(true);

      // Server-owned identity + invite/org fields: persistInvite (Admin) + shared baseline callable
      await ensureUserIdentityBaseline({
        source:
          authMethod === "email"
            ? IdentityBaselineSource.CREATE_ACCOUNT_EMAIL
            : IdentityBaselineSource.CREATE_ACCOUNT_GOOGLE
      });

      // Client may only merge fields allowed by firestore.rules (lastLogin + private/pii)
      const resolvedFirst =
        authMethod === "google" ? user.displayName?.split(" ")[0] || firstName : firstName;
      const resolvedLast =
        authMethod === "google"
          ? user.displayName?.split(" ").slice(1).join(" ") || lastName
          : lastName;

      await mergeUserAllowedFieldsAfterSignup({
        uid: user.uid,
        firstName: resolvedFirst,
        lastName: resolvedLast,
        lastLogin: new Date()
      });
      
      // ENFORCE ALLOWLIST (KB-3133). Until this line, `/create-account` created
      // an account and walked it into the app with no role check anywhere on
      // the path — the same hole as the email/password door, one page over.
      //
      // ⚠️ THE POSITION OF THIS CALL IS THE WHOLE DESIGN. It is deliberately
      // BELOW `persistInviteToAccountIfNeeded` and the token refresh above,
      // because that call is what GRANTS the role this gate reads:
      // `persistInviteToAccount` writes `users/{uid}.role` from the invitation
      // (`functions/src/invitations/persistInviteToAccount.ts:50-61`) and
      // refuses to run for an anonymous caller (`:15-17`), so it cannot happen
      // any earlier than this. A genuinely-approved applicant carries
      // `role: 'none'` right up until it lands.
      //
      // Move this gate above that call and it refuses 100% of legitimate
      // invitees while still admitting nobody it should — the access-narrowing
      // trap in dispatch-SOP §2a item 2, where a read that runs before a role
      // exists cannot satisfy any role-based carve-out.
      //
      // Someone who reached this page without a usable invitation ends here
      // with `role: 'none'` and is refused, in the same words as the other two
      // doors.
      const verdict = await enforceProAccessGate(user.uid, "create-account");
      if (verdict.outcome !== "allowed") {
        return false;
      }

      // KB-3235: mint the apex `__session` cookie now that the gate has allowed
      // this newly-created (and role-granted) account. AFTER the gate;
      // fire-and-forget so it cannot fail or delay the redirect. See
      // `apexSessionMint.ts`.
      void mintApexSession(user);

      // Set a flag to help detect account creation flow
      localStorage.setItem('kb_account_creation_flow', 'true');

      // Redirect to welcome page with success parameter and loading flag
      if (token) {
        navigate(`/welcome?token=${token}&success=account-created&loading=auth`, {
          state: { fromAccountCreation: true },
          replace: true
        });
      } else {
        navigate('/welcome?success=account-created&loading=auth', {
          state: { fromAccountCreation: true },
          replace: true
        });
      }

      return true;
    } catch (error) {
      console.error("Error creating user account:", error);
      throw error;
    }
  };

  const handleEmailSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      const auth = getAuth();
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Send email verification with environment-aware continueUrl
      await sendEmailVerificationWithContinueUrl(userCredential.user);
      
      // Update display name
      await updateProfile(userCredential.user, {
        displayName: `${firstName} ${lastName}`
      });

      const admitted = await createUserAccount(userCredential.user, "email");

      // Guarded on the verdict: the gate has already signed a refused caller
      // out and told them their account is not authorized. Announcing "Account
      // created successfully!" over the top of that would be the reassuring,
      // plausible, wrong sentence SOP §6a-bis forbids — and it would be the
      // LAST thing on screen, since this toast is not sticky and the refusal is.
      if (!admitted) {
        return;
      }

      // Show success toast for email verification
      toast.success(
        <div>
          <div>Account created successfully!</div>
          <div className="text-sm text-gray-600 mt-1">Please check your email and click the link to verify your email address.</div>
        </div>,
        { duration: 12000 }
      );
    } catch (error: any) {
      console.error("Error creating account:", error);
      if (error.code === 'auth/email-already-in-use') {
        toast.error("An account with this email already exists. Please sign in instead.");
      } else {
        toast.error("Failed to create account. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    // Only validate terms checkbox for Google signup, not the form fields
    if (!agreeToTerms) {
      setErrors({ agreeToTerms: "You must agree to the Terms of Use" });
      return;
    }
    
    setIsGoogleLoading(true);
    
    try {
      // Get claims from current anonymous user before Google signup
      const tokenResult = await currentUser!.getIdTokenResult();
      const auth = getAuth();
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // KB-3133 round-1 finding 6: the verdict is READ, not discarded, even
      // though nothing follows this line today. The sibling call at `:231`
      // needs `if (!admitted) return;` precisely because something does follow
      // it there — so the next line anyone adds here inherits a bug that the
      // gate itself cannot catch, in a file where two doors sit 47 lines apart
      // and only one of them checked. The symmetry IS the fix.
      const admitted = await createUserAccount(result.user, "google");
      if (!admitted) {
        return;
      }
    } catch (error: any) {
      console.error("Error with Google signup:", error);
      if (error.code === 'auth/account-exists-with-different-credential') {
        toast.error("An account with this email already exists. Please sign in instead.");
      } else {
        toast.error("Failed to create account with Google. Please try again.");
      }
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleTermsCheckbox = (checked: boolean) => {
    // Only allow agreement via explicit "I Agree" in the modal
    if (checked) {
      // If not already agreed, open the modal instead of checking immediately
      if (!agreeToTerms) {
        setShowTermsModal(true);
      }
    } else {
      // Unchecking always clears agreement
      setAgreeToTerms(false);
    }
  };

  const handleTermsModalClose = () => {
    // Closing without clicking "I Agree" should NOT check the box
    setShowTermsModal(false);
    setHasOpenedTerms(true);
  };

  const handleLoginClick = async () => {
    try {
      // Clear any stored invitation token
      localStorage.removeItem("kb_invite_token");

      // Sign out the current user.
      // KB-3270: `{ federated: false }` — "Log in instead" is a redirect out of
      // the create-account flow, not a logout. The person may be a legitimate
      // kidsbooked.com consumer who followed a link here, so we must not clear
      // their apex session cookie (same reasoning as ProtectedRoute's eject).
      await signOut({ federated: false });

      // Navigate to login page
      navigate("/login");
    } catch (error) {
      console.error("Error during logout:", error);
      // Even if logout fails, still navigate to login
      navigate("/login");
    }
  };

  const isFormValid = email && firstName && lastName && password && confirmPassword && agreeToTerms && (!hasAttemptedSubmit || !Object.keys(errors).length);

  return (
    <>
      <div className="min-h-screen bg-background flex flex-col">
        <div className="flex-grow flex items-center justify-center bg-kidsbooked-lightGray pt-5" style={{ paddingBottom: '10px' }}>
          <Card className="w-full max-w-md my-1">
            <CardHeader className="space-y-1 flex flex-col items-center">
              <WebsiteLogo width={240} className="mb-4" />
              <CardTitle className="text-2xl font-bold">Create Account</CardTitle>
            </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Organization name at the top */}
              <div className="space-y-2">
                <label htmlFor="organizationName" className="text-sm font-medium">
                  Organization name
                </label>
                <Input
                  id="organizationName"
                  value={organizationName}
                  readOnly
                  className="bg-gray-50 cursor-not-allowed text-gray-500"
                />
              </div>

              {/* Terms checkbox */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="agreeToTerms"
                  checked={agreeToTerms}
                  onCheckedChange={handleTermsCheckbox}
                />
                <label htmlFor="agreeToTerms" className="text-sm">
                  I agree to the{" "}
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(true)}
                    className="text-kidsbooked-blue hover:underline"
                  >
                    Terms of Use
                  </button>{" "}
                  <span className="text-red-500">*</span>
                </label>
              </div>
              {hasAttemptedSubmit && errors.agreeToTerms && (
                <p className="text-sm text-red-500">{errors.agreeToTerms}</p>
              )}

              {/* Google signup button */}
              <Button 
                type="button"
                className="w-full btn-primary"
                onClick={handleGoogleSignup}
                disabled={!agreeToTerms || isGoogleLoading}
              >
                {isGoogleLoading ? "Signing up..." : "Continue with Google"}
              </Button>

              {/* OR divider */}
              <div className="flex items-center justify-center space-x-4 my-6">
                <div className="flex-1 border-t border-gray-300"></div>
                <span className="text-sm text-gray-500 font-medium">OR</span>
                <div className="flex-1 border-t border-gray-300"></div>
              </div>

              {/* Manual form fields */}
              <form onSubmit={handleEmailSignup} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium">
                    Your email address <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="email"
                    placeholder="name@example.com"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className={hasAttemptedSubmit && errors.email ? "border-red-500" : ""}
                  />
                  {hasAttemptedSubmit && errors.email && (
                    <p className="text-sm text-red-500">{errors.email}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label htmlFor="firstName" className="text-sm font-medium">
                    First name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="firstName"
                    placeholder="John"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    className={hasAttemptedSubmit && errors.firstName ? "border-red-500" : ""}
                  />
                  {hasAttemptedSubmit && errors.firstName && (
                    <p className="text-sm text-red-500">{errors.firstName}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label htmlFor="lastName" className="text-sm font-medium">
                    Last name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="lastName"
                    placeholder="Doe"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    className={hasAttemptedSubmit && errors.lastName ? "border-red-500" : ""}
                  />
                  {hasAttemptedSubmit && errors.lastName && (
                    <p className="text-sm text-red-500">{errors.lastName}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label htmlFor="password" className="text-sm font-medium">{/* SECURITY_AUDIT_PASS: label uses keyword 'password'; no secret */}
                    Password <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className={hasAttemptedSubmit && errors.password ? "border-red-500" : ""}
                  />
                  {hasAttemptedSubmit && errors.password && (
                    <p className="text-sm text-red-500">{errors.password}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label htmlFor="confirmPassword" className="text-sm font-medium">
                    Confirm password <span className="text-red-500">*</span>{/* SECURITY_AUDIT_PASS: static label text; no secret */}
                  </label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className={hasAttemptedSubmit && errors.confirmPassword ? "border-red-500" : ""}
                  />
                  {hasAttemptedSubmit && errors.confirmPassword && (
                    <p className="text-sm text-red-500">{errors.confirmPassword}</p>
                  )}
                </div>

                {/* Mirrored Terms checkbox (quantum entangled with the top one) */}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="agreeToTermsBottom"
                    checked={agreeToTerms}
                    onCheckedChange={handleTermsCheckbox}
                  />
                  <label htmlFor="agreeToTermsBottom" className="text-sm">
                    I agree to the{" "}
                    <button
                      type="button"
                      onClick={() => setShowTermsModal(true)}
                      className="text-kidsbooked-blue hover:underline"
                    >
                      Terms of Use
                    </button>{" "}
                    <span className="text-red-500">*</span>
                  </label>
                </div>
                {hasAttemptedSubmit && errors.agreeToTerms && (
                  <p className="text-sm text-red-500">{errors.agreeToTerms}</p>
                )}

                <Button 
                  type="submit" 
                  className="w-full btn-primary"
                  disabled={!isFormValid || isLoading}
                >
                  {isLoading ? "Creating Account..." : "Create Account"}
                </Button>
              </form>
            </div>
          </CardContent>
        </Card>
        </div>
        
        {/* Login link in gray background area */}
        <div className="flex justify-center pt-1 bg-kidsbooked-lightGray" style={{ paddingBottom: '60px' }}>
          <span className="text-sm text-muted-foreground">
            Already have an account?{" "}
            <button 
              onClick={handleLoginClick}
              className="text-kidsbooked-blue hover:underline font-medium"
            >
              Login here
            </button>
          </span>
        </div>
        
        {/* Custom footer with light gray background to match page */}
        <footer className="w-full bg-kidsbooked-lightGray border-t py-6 mt-auto">
          <div className="container max-w-screen-xl mx-auto px-4">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 pb-2">
              
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
                <span className="text-muted-foreground">
                  Need help? Contact <a href="mailto:support@kidsbooked.com" className="underline hover:text-kidsbooked-blue transition-colors">support@kidsbooked.com</a>
                </span>
                <Separator orientation="vertical" className="h-4 hidden md:block" />
                <a href="https://kidsbooked.com/about/" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-kidsbooked-blue transition-colors">
                  About KidsBooked
                </a>
                <Separator orientation="vertical" className="h-4 hidden md:block" />
                <a href="https://kidsbooked.com/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-kidsbooked-blue transition-colors">
                  Privacy policy
                </a>
                <Separator orientation="vertical" className="h-4 hidden md:block" />
                <button onClick={() => setShowFooterTermsModal(true)} className="text-muted-foreground hover:text-kidsbooked-blue transition-colors">
                  Terms of use
                </button>
              </div>
              <div className="text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Copyright size={14} />
                  <span>2025 KidsBooked, LLC</span>
                </div>
              </div>
              
            </div>
          </div>
        </footer>
      </div>

      {/* Interactive modal for form terms agreement */}
      <TermsOfUseModal 
        isOpen={showTermsModal} 
        onClose={handleTermsModalClose}
        onAccept={() => {
          setAgreeToTerms(true);
          setHasOpenedTerms(true);
          setShowTermsModal(false);
        }}
      />
      
      {/* Read-only modal for footer terms viewing */}
      <TermsOfUseModal 
        isOpen={showFooterTermsModal} 
        onClose={() => setShowFooterTermsModal(false)}
      />
    </>
  );
};

export default CreateAccountPage;
